import React, { useEffect, useContext, useMemo, useState, useRef, useCallback } from "react";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Input } from "../../ui/input";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { Check, Loader2 } from "lucide-react";
import { useFormik } from "formik";
import * as yup from "yup";
import SharedDataContext from "../../../SharedDataContext";
import Image from "../../Tags/Image";
import { cn } from "../../../lib/utils";
export default function AnalyticsHeader({
  filters,
  onFilterChange,
  activeTab,
  userAccessData,
  isLoadingUsers,
  onLoadMoreUsers,
}) {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const [customDates, setCustomDates] = useState({ from: null, to: null });
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);

  useEffect(() => {
    console.log("Custom date popover state:", isCustomDateOpen);
  }, [isCustomDateOpen]);

  const userOptions = useMemo(() => {
    if (!userAccessData?.data) {
      return [];
    }

    const users = userAccessData.data.map((user) => ({
      value: user.id,
      label: `${user.name}`,
      userId: user.id,
    }));

    return users;
  }, [userAccessData]);

  useEffect(() => {
    if (!filters?.userTeamSelector && userOptions.length > 0) {
      onFilterChange("userTeamSelector", userOptions[0].value);
    }
  }, [filters?.userTeamSelector, userOptions]);

  useEffect(() => {
    if (filters?.viewToggle === "Individuals" && userOptions.length > 0 && !filters?.userTeamSelector) {
      onFilterChange("userTeamSelector", userOptions[0].value);
    }
  }, [filters?.viewToggle, filters?.userTeamSelector, userOptions]);

  // Infinite scroll
  const handleLoadMore = useCallback(async () => {
    if (!userAccessData?.pagination || isLoadingMore || !onLoadMoreUsers) {
      return;
    }

    const { currentPage, lastPage } = userAccessData.pagination;

    if (currentPage >= lastPage) {
      return;
    }

    setIsLoadingMore(true);
    try {
      await onLoadMoreUsers(currentPage + 1);
    } catch (error) {
      console.error("Error loading more users:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [userAccessData?.pagination, isLoadingMore, onLoadMoreUsers]);

  useEffect(() => {
    if (!userDropdownOpen) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!loadMoreTriggerRef.current) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !isLoadingMore) {
              handleLoadMore();
            }
          });
        },
        {
          root: scrollContainerRef.current,
          rootMargin: "50px",
          threshold: 0.1,
        }
      );

      const currentTrigger = loadMoreTriggerRef.current;
      if (currentTrigger) {
        observer.observe(currentTrigger);
      }

      return () => {
        if (currentTrigger) {
          observer.unobserve(currentTrigger);
        }
        observer.disconnect();
      };
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [userDropdownOpen, handleLoadMore, isLoadingMore, userAccessData?.pagination]);

  console.log("userOptions", userOptions);
  const selectedUserValue =
    filters?.userTeamSelector || (userOptions.length > 0 ? userOptions[0].value : null);
  const selectedUserLabel =
    userOptions.find((o) => o.value === selectedUserValue)?.label ||
    (userOptions.length > 0 ? userOptions[0].label : "No users available");

  const getDisplayLabel = () => {
    if (filters.viewToggle === "Team") {
      return activeWorkspace?.name || "Workspace";
    }
    return selectedUserLabel;
  };

  const shouldShowDropdown = filters.viewToggle === "Individuals";

  const dateRangeOptions = [
    { value: "7 days", label: "7 days" },
    { value: "30 days", label: "30 days" },
    { value: "60 days", label: "60 days" },
    { value: "90 days", label: "90 days" },
    { value: "180 days", label: "180 days" },
    { value: "1 year", label: "1 year" },
    { value: "custom", label: "Custom range" },
  ];

  // Custom date validation schema
  const validationSchema = yup.object().shape({
    from_date: yup.string().required("From date is required"),
    to_date: yup.string().required("To date is required"),
  });

  const formik = useFormik({
    initialValues: {
      from_date: "",
      to_date: "",
    },
    validationSchema,
    onSubmit: (values) => {
      onFilterChange("dateRange", "custom");
      onFilterChange("customStartDate", values.from_date);
      onFilterChange("customEndDate", values.to_date);

      setCustomDates({
        from: values.from_date,
        to: values.to_date,
      });

      setIsCustomDateOpen(false);
      formik.setSubmitting(false);
    },
  });

  useEffect(() => {
    if (filters.customStartDate && filters.customEndDate) {
      setCustomDates({
        from: filters.customStartDate,
        to: filters.customEndDate,
      });
      formik.setValues({
        from_date: filters.customStartDate,
        to_date: filters.customEndDate,
      });
    }
  }, [filters.customStartDate, filters.customEndDate]);

  const callDirectionOptions = [
    { value: "Outgoing", label: "Outbound" },
    { value: "Incoming", label: "Inbound" },
    { value: "All", label: "All calls" },
  ];

  return (
    <div className="bg-[#FFFFFF] dark:bg-[#0C111D] border  dark:border-[#1F242F] p-8 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-purple-600">
                {getDisplayLabel()
                  .split(" ")
                  .map((word) => word.charAt(0))
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
              </span>
            </div>
            {shouldShowDropdown ? (
              <Popover open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
                <PopoverTrigger asChild disabled={isLoadingUsers}>
                  <button
                    className="flex items-center gap-4 text-[#181D27] dark:text-white font-semibold focus:outline-none disabled:opacity-50"
                    disabled={isLoadingUsers}
                  >
                    <span>{isLoadingUsers ? "Loading users..." : selectedUserLabel}</span>
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[300px]" align="start">
                  <Command>
                    <div className="p-2">
                      <div className="border rounded-[8px] border-b-0 overflow-hidden">
                        <CommandInput placeholder="Search users..." />
                      </div>
                    </div>
                    <CommandList ref={scrollContainerRef} className="max-h-[300px] overflow-y-auto">
                      <CommandEmpty>No users found.</CommandEmpty>
                      <CommandGroup>
                        {userOptions.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={`${option.value}-${option.label}`}
                            keywords={[option.label]}
                            onSelect={() => {
                              onFilterChange("userTeamSelector", option.value);
                              setUserDropdownOpen(false);
                            }}
                            className="min-h-[40px]"
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedUserValue === option.value ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>

                      {userAccessData?.pagination &&
                        userAccessData.pagination.currentPage < userAccessData.pagination.lastPage && (
                          <div
                            ref={loadMoreTriggerRef}
                            className="py-4 flex justify-center items-center border-t border-gray-100"
                          >
                            {isLoadingMore ? (
                              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                            ) : (
                              <span className="text-xs text-gray-400">Scroll to load more...</span>
                            )}
                          </div>
                        )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="flex items-center gap-4 text-[#181D27] dark:text-white font-semibold">
                <span>{getDisplayLabel()}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Tabs
            value={filters.viewToggle === "Team" ? "team" : "individuals"}
            onValueChange={(v) => onFilterChange("viewToggle", v === "team" ? "Team" : "Individuals")}
            className="w-auto"
          >
            <TabsList className="bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg p-0">
              <TabsTrigger value="team" className="px-4 py-2 text-sm text-[#717680] font-semibold">
                Workspace
              </TabsTrigger>
              <TabsTrigger value="individuals" className="px-4 py-2 text-sm text-[#717680] font-semibold">
                Individuals
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <Select
            value={filters.dateRange}
            onValueChange={(value) => {
              console.log("Date range selected:", value);
              if (value === "custom") {
                console.log("Opening custom date picker");
                onFilterChange("dateRange", "custom");
                setIsCustomDateOpen(true);
              } else {
                onFilterChange("dateRange", value);

                onFilterChange("customStartDate", undefined);
                onFilterChange("customEndDate", undefined);
                setCustomDates({ from: null, to: null });
              }
            }}
          >
            <SelectTrigger className="w-full bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg [&>svg]:hidden">
              <div className="flex items-center gap-12 text-[#181D27] dark:text-white font-medium">
                <div className="flex items-center gap-2">
                  <Image
                    src="/images/icons/untitledui/call-activity/calender.svg"
                    alt="Calendar"
                    className="h-4 w-4"
                  />
                  {filters.dateRange === "custom" && customDates.from && customDates.to ? (
                    <span>
                      Custom Range : {customDates.from} - {customDates.to}
                    </span>
                  ) : (
                    <SelectValue placeholder="Select range" />
                  )}
                </div>
                <ChevronDownIcon className="h-4 w-4 text-[#181D27] ml-auto" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {dateRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isCustomDateOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
              onClick={() => setIsCustomDateOpen(false)}
            >
              <div
                className="bg-white dark:bg-[#181D27] dark:text-white rounded-lg shadow-lg p-6 w-80 max-w-sm mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={formik.handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium leading-none">Custom Date Range</h4>
                    <p className="text-sm text-muted-foreground">Select a custom date range for analytics.</p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="from_date" className="block text-sm font-medium text-gray-700 mb-1">
                        From:
                      </label>
                      <Input
                        id="from_date"
                        name="from_date"
                        type="date"
                        min="2022-01-01"
                        max={new Date().toISOString().split("T")[0]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.from_date}
                        className="w-full"
                      />
                      {formik.touched.from_date && formik.errors.from_date && (
                        <p className="mt-1 text-red-500 text-sm">{formik.errors.from_date}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="to_date" className="block text-sm font-medium text-gray-700 mb-1">
                        To:
                      </label>
                      <Input
                        id="to_date"
                        name="to_date"
                        type="date"
                        min="2022-01-01"
                        max={new Date().toISOString().split("T")[0]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.to_date}
                        className="w-full"
                      />
                      {formik.touched.to_date && formik.errors.to_date && (
                        <p className="mt-1 text-red-500 text-sm">{formik.errors.to_date}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsCustomDateOpen(false);
                        formik.resetForm();

                        onFilterChange("dateRange", "7 days");
                        onFilterChange("customStartDate", undefined);
                        onFilterChange("customEndDate", undefined);
                        setCustomDates({ from: null, to: null });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        formik.isSubmitting ||
                        !formik.isValid ||
                        !formik.values.from_date ||
                        !formik.values.to_date
                      }
                    >
                      Apply
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === "activity" && (
            <Select
              value={filters.callDirection}
              onValueChange={(value) => onFilterChange("callDirection", value)}
            >
              <SelectTrigger className=" bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg [&>svg]:hidden">
                <div className="flex items-center gap-12 text-[#181D27] dark:text-white font-medium">
                  <div className="flex items-center gap-2">
                    <Image
                      src="/images/icons/untitledui/call-activity/phone-icon.svg"
                      alt="Phone"
                      className="h-4 w-4"
                    />
                    <SelectValue />
                  </div>
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 ml-auto" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {callDirectionOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">{option.label}</div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </div>
  );
}
